import { Component, Input, OnChanges } from '@angular/core';
import { UfosRecord } from './ufos.types';

interface SourceField {
  key: string;
  label: string;
  value: string;
  kind: 'text' | 'boolean' | 'status';
  booleanValue: boolean;
  meaningful: boolean;
  matched: boolean;
}

@Component({
  selector: 'app-ufos-record',
  templateUrl: './ufos-record.component.html',
  styleUrls: ['./ufos-record.component.scss']
})
export class UfosRecordComponent implements OnChanges {
  @Input() record: UfosRecord = {};
  @Input() label = 'Source record';
  @Input() fields: string[] = [];
  @Input() search = '';
  @Input() heading = '';
  @Input() detailsOnly = false;

  visibleFields: SourceField[] = [];
  allFields: SourceField[] = [];
  rawJson = '';
  sourceView: 'fields' | 'json' = 'fields';
  sourceOpen = false;
  hiddenPreviewCount = 0;
  matchCount = 0;
  warningCount = 0;
  hasSearch = false;

  private readonly fieldLabels: Record<string, string> = {
    wo_number: 'Work order', wo_id: 'Work-order ID', woload_id: 'Load ID', woloadid: 'Load ID',
    load_no: 'Load number', cust_id: 'Customer ID', cust_name: 'Customer',
    carr_id: 'Carrier ID', car_name: 'Carrier', carrname: 'Carrier',
    ship_id: 'Shipper ID', shipname: 'Shipper', cons_id: 'Consignee ID', cons_name: 'Consignee',
    c_currency: 'Customer currency', carr_currency: 'Carrier currency', expcurrency: 'Expense currency',
    date_enter: 'Entered date', last_updat: 'Last updated', status2: 'Secondary status',
    inv_number: 'Invoice number', invoiceno: 'Invoice number', carrinvoiceno: 'Carrier invoice',
    ship_act_date: 'Actual pickup date', cons_act_date: 'Actual delivery date',
    ship_eta_date: 'Scheduled pickup date', cons_eta_date: 'Scheduled delivery date',
    pod_name: 'POD name', pod_date: 'POD date', po_number: 'PO number',
    lbs: 'Weight (lb)', kgs: 'Weight (kg)', desc: 'Description', descr: 'Description',
    _ufos_recno: 'Source row', _ufos_deleted: 'Deleted in source',
    _ufos_warnings: 'Source warnings', _ufos_record_marker: 'Source record marker',
    _omitted_fields: 'Omitted fields'
  };

  ngOnChanges(): void {
    const search = this.search.trim().toLowerCase();
    this.hasSearch = search !== '';
    this.allFields = Object.entries(this.record).map(([key, value]): SourceField => {
      const label = this.fieldLabel(key);
      const text = this.display(value);
      return {
        key, label, value: text,
        kind: typeof value === 'boolean' ? 'boolean' : /(^|_)status\d*$/.test(key) ? 'status' : 'text',
        booleanValue: value === true,
        meaningful: this.isMeaningful(value),
        matched: this.hasSearch && `${key} ${label} ${text}`.toLowerCase().includes(search)
      };
    });
    const byKey = new Map(this.allFields.map(field => [field.key, field]));
    const candidates = this.hasSearch
      ? this.allFields.filter(field => field.matched)
      : (this.fields.length
        ? this.fields.map(key => byKey.get(key)).filter((field): field is SourceField => !!field)
        : this.allFields.filter(field => !field.key.startsWith('_'))
      ).filter(field => field.meaningful);
    this.matchCount = this.hasSearch ? candidates.length : 0;
    this.visibleFields = candidates.slice(0, 6);
    this.hiddenPreviewCount = Math.max(0, candidates.length - this.visibleFields.length);
    this.rawJson = JSON.stringify(this.record, null, 2);
    this.warningCount = this.countWarnings(this.record['_ufos_warnings']);
  }

  get isDeleted(): boolean {
    return [true, 1, '1', 't', 'true'].includes(this.record['_ufos_deleted'] as boolean | number | string);
  }

  display(value: unknown): string {
    if (value === null || value === undefined) return 'Not recorded';
    if (typeof value === 'string' && value.trim() === '') return '(blank)';
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  trackField(_index: number, field: SourceField): string { return field.key; }

  onDisclosureToggle(event: Event): void {
    this.sourceOpen = (event.target as HTMLDetailsElement).open;
  }

  private fieldLabel(key: string): string {
    if (this.fieldLabels[key]) return this.fieldLabels[key];
    const label = key.replace(/^_+/, '').replace(/_/g, ' ').replace(/\b(uuid|id|api|edi|po|pod)\b/gi, value => value.toUpperCase());
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  private isMeaningful(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  }

  private countWarnings(value: unknown): number {
    if (typeof value === 'string') {
      try { return this.countWarnings(JSON.parse(value)); }
      catch { return value.trim() ? 1 : 0; }
    }
    if (Array.isArray(value)) return value.length;
    if (value && typeof value === 'object') return Object.keys(value).length;
    return value ? 1 : 0;
  }
}
